import {
  Body,
  Button,
  Caption,
  Flex,
  Form,
  Image,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Shape,
  Textarea,
  styled,
  useToast,
} from '@teko/ds-desktop';
import { RatingFilled } from '@teko/ds-icon';
import { logger } from '@teko/log';
import { ACCEPTED_IMAGE_TYPES } from 'constants/index';
import { useProductDetailState, useTenantConfig } from 'contexts';
import { t } from 'i18n';
import { RcFile } from 'rc-upload/lib/interface';
import { useRef, useState } from 'react';
import { createProductReview } from 'services';
import {
  IMediaUploaderProps,
  MediaUploader,
  UploadFileResponse,
} from './MediaUploader';

const MAXIMUM_COMMENT_LENGTH = 2000;
const MAX_NUMBER_OF_MEDIA_ITEMS = 5;

interface IProductReviewProps {
  visible: boolean;
  onCloseModal: () => void;
  openSuccessModal: () => void;
  refetchReviewList: () => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const ProductReview: React.FC<IProductReviewProps> = ({
  visible,
  onCloseModal,
  openSuccessModal,
  refetchReviewList,
}) => {
  const toast = useToast();
  const [form] = Form.useForm();
  const { isMobile } = useTenantConfig();
  const { product } = useProductDetailState();

  const [rating, setRating] = useState<number>();
  const [commentLength, setCommentLength] = useState(0);
  const [fileList, setFileList] = useState<UploadFileResponse[]>([]);
  const toastShownRef = useRef(false);

  const isUploading = fileList.some((file) => file.status === 'uploading');

  const handleBeforeUpload = (file: RcFile, fileListIncoming?: RcFile[]) => {
    const incoming = fileListIncoming?.length ?? 1;

    if (fileList.length + incoming > MAX_NUMBER_OF_MEDIA_ITEMS) {
      if (!toastShownRef.current) {
        toastShownRef.current = true;

        toast?.error?.({
          title: t('Cảnh báo'),
          placement: 'topRight',
          message: t(`Cho phép tối đa {{maxNumber}} ảnh/video.`, {
            maxNumber: MAX_NUMBER_OF_MEDIA_ITEMS,
          }),
        });

        setTimeout(() => {
          toastShownRef.current = false;
        }, 0);
      }

      return false;
    }

    if (
      !file.type ||
      (!ACCEPTED_IMAGE_TYPES.includes(file.type) &&
        !file.type.startsWith('video'))
    ) {
      toast?.error?.({
        title: t('Có lỗi'),
        placement: 'topRight',
        message: t('File không hợp lệ'),
      });
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast?.error?.({
        title: t('Có lỗi'),
        placement: 'topRight',
        message: t('File không được vượt quá {{max}}MB', { max: 10 }),
      });
      return false;
    }
    const url = URL.createObjectURL(file);
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    setFileList((prev) => [
      ...prev,
      {
        file,
        url,
        type: isImage ? 'image' : isVideo ? 'video' : 'other',
        status: 'uploading',
      },
    ]);
    return true;
  };

  const uploaderProps: IMediaUploaderProps = {
    beforeUpload: handleBeforeUpload,
    onSuccess: (res, file) => {
      setFileList((prev) =>
        prev.map((item) =>
          item.file === file
            ? {
                ...item,
                status: 'done',
                url: (res as Pick<UploadFileResponse, 'image_url' | 'url'>).url,
                image_url: (
                  res as Pick<UploadFileResponse, 'image_url' | 'url'>
                ).image_url,
              }
            : item,
        ),
      );
    },
    onDelete: (fileUrl) =>
      setFileList(fileList.filter((file) => file.url !== fileUrl)),
    fileList,
    onError(_, file) {
      const newFileList = fileList.filter((item) => item.file.uid === file.uid);
      setFileList(newFileList);
    },
    maxItems: MAX_NUMBER_OF_MEDIA_ITEMS,
  };

  const handleCreateProductReview = async () => {
    if (product?.productGroup?.id && product.seller?.id && rating) {
      const getMediaUrls = (type: string) => {
        return fileList
          .filter((file) => file.type === type)
          .map((item) => item.url);
      };

      try {
        const payload = {
          productUrl: window?.location?.href,
          rating,
          sellerId: product.seller.id,
          review: {
            comment: form.getFieldValue('comment'),
            images: getMediaUrls('image'),
            videos: getMediaUrls('video'),
          },
        };
        await createProductReview(product.productGroup.id, payload);
        onCloseModal();
        openSuccessModal();
        refetchReviewList();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        const traceId = e?.response?.error?.traceId;
        toast.error?.({
          title: t('Có lỗi'),
          message: `[${traceId}] ${t('Có lỗi xảy ra. Vui lòng thử lại sau!')}`,
        });
        logger.error(`Error occur when createProductReview: ${e}`);
      }
    } else {
      logger.error(
        'Missing productId or sellerId or rating',
        product?.productGroup?.id,
        product?.seller?.id,
        rating,
      );
    }
  };

  return (
    <Modal
      visible={visible}
      borderRadius="16px"
      width={isMobile ? '90%' : 480}
      showCloseIcon={true}
      onClose={onCloseModal}
      okText=""
      cancelText=""
      contentStyle={{ padding: 0 }}
      footer={null}
    >
      <ModalHeader>{t('Đánh giá sản phẩm')}</ModalHeader>
      <ModalBody paddingLeft="s16" paddingRight="s16">
        <Flex
          border="1px solid divider"
          borderRadius="m"
          align="center"
          padding="s8"
        >
          <ProductImage>
            <StyleImage src={product?.images?.[0].src || ''} />
          </ProductImage>
          <Flex direction="column" marginLeft="s16">
            <Caption level={2}>{product?.displayName}</Caption>
          </Flex>
        </Flex>
        <Shape
          marginTop="s16"
          padding={['s16', 's24']}
          backgroundColor="#fafafa"
        >
          <Flex
            align="center"
            justify="center"
            direction="column"
            marginBottom="s24"
          >
            <StyledBody level={1} marginBottom="s8">
              {t('Bạn có hài lòng về sản phẩm?')}
            </StyledBody>
            <StarRating>
              {Array(5)
                .fill(null)
                .map((_, index) => (
                  <RatingFilled
                    key={index}
                    size={isMobile ? 28 : 32}
                    style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}
                    onClick={() => setRating(index + 1)}
                    color={
                      rating && index + 1 <= rating ? '#fcde04' : '#C4D1FF80'
                    }
                  />
                ))}
            </StarRating>
          </Flex>
          <Form layout="vertical" form={form}>
            <Form.Item
              label={
                <StyledBody level={1}>
                  {t('Chia sẻ nhận xét về sản phẩm')}
                </StyledBody>
              }
              style={{ marginBottom: 0 }}
              name="comment"
            >
              <Textarea
                rows={4}
                borderRadius="l"
                maxLength={MAXIMUM_COMMENT_LENGTH}
                onChange={(value) => setCommentLength(value.length)}
              />
            </Form.Item>
          </Form>
          <Flex justify="space-between" align="start" marginTop="s0">
            <MediaUploader {...uploaderProps}>
              <StyledBody level={1} color="link">
                {t('Thêm ảnh/video')}
              </StyledBody>
            </MediaUploader>
            {(!isMobile || fileList.length < MAX_NUMBER_OF_MEDIA_ITEMS - 1) && (
              <StyledBody level={1} color="#8C8C8C">
                {`${commentLength}/${MAXIMUM_COMMENT_LENGTH}`}
              </StyledBody>
            )}
          </Flex>
        </Shape>
      </ModalBody>
      <ModalFooter paddingLeft="s40" paddingRight="s40">
        <Button
          width={'100%'}
          variant="primary"
          borderRadius="m"
          onClick={handleCreateProductReview}
          disabled={isUploading}
        >
          {t('Gửi đánh giá')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const ProductImage = styled.div`
  width: 40px;
  height: 30px;
  flex-shrink: 0;
  align-items: center;
  justify-self: center;
`;

const StarRating = styled.div`
  cursor: pointer;
`;

const StyleImage = styled(Image)`
  height: 100%;
  width: 100%;
  object-fit: cover;
  display: block;
`;

const StyledBody = styled(Body)`
  line-height: 22px;
  font-size: 14px;
`;
